import i18n from "i18next";
import Backend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import { getWhiteLabel } from "./white-label";

const fallbackLng = "en_US";

export const getLocaleFromSearchParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("locale");
};

const convertLocaleFromWhiteLabelToTransifexFormat = (
  whiteLabelLocale: string,
) => {
  const parts = whiteLabelLocale.split("-");
  if (parts.length === 2) {
    return `${parts[0]}_${parts[1]!.toUpperCase()}`;
  }
  return whiteLabelLocale;
};

export const getInitialLocale = () => {
  const searchParamLocale = getLocaleFromSearchParams();
  if (searchParamLocale) {
    return searchParamLocale;
  }

  const whiteLabelLocale = getWhiteLabel().locale;
  const convertedLocale =
    convertLocaleFromWhiteLabelToTransifexFormat(whiteLabelLocale);
  const params = new URLSearchParams(window.location.search);
  params.set("locale", convertedLocale);
  window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  return convertedLocale;
};

export const setTranslation = async () => {
  // eslint-disable-next-line import/no-named-as-default-member
  i18n
    .use(Backend)
    .use(initReactI18next)
    .init({
      fallbackLng,
      lng: getInitialLocale(),
      debug: false,
      ns: ["esim:layout", "esim:purchase-form"],
      partialBundledLanguages: true,
      react: {
        useSuspense: false,
      },
      backend: {
        loadPath: "/esim/locales/{{lng}}/{{ns}}.json",
      },
    });
};

export const URL_NAMES = {
  LEARN_ESIM: "LEARN_ESIM",
  COMPATIBLE_DEVICE: "COMPATIBLE_DEVICE",
  FAQ: "FAQ",
} as const;

type UrlNames = (typeof URL_NAMES)[keyof typeof URL_NAMES];

type Locale =
  | "en_US"
  | "it_IT"
  | "nl"
  | "fr"
  | "de_DE"
  | "no_NO"
  | "da_DK"
  | "fi_FI"
  | "sv";

const urlMap: Record<UrlNames, Record<Locale, string>> = {
  [URL_NAMES.LEARN_ESIM]: {
    en_US:
      "https://moziogroup.zendesk.com/hc/en-us/articles/12379430062093-How-does-it-work-",
    it_IT:
      "https://moziogroup.zendesk.com/hc/it-it/articles/12379430062093-Come-funziona-",
    nl: "https://moziogroup.zendesk.com/hc/nl/articles/22530359524237-Hoe-werkt-het",
    fr: "https://moziogroup.zendesk.com/hc/fr-fr/articles/22505650896781-Comment-%C3%A7a-marche",
    de_DE:
      "https://moziogroup.zendesk.com/hc/de/articles/23657854502925-Wie-funktioniert-das",
    no_NO:
      "https://moziogroup.zendesk.com/hc/no/articles/24911561296269-Hvordan-fungerer-det",
    da_DK:
      "https://moziogroup.zendesk.com/hc/da-dk/articles/25053136382477-Hvordan-fungerer-det",
    fi_FI:
      "https://moziogroup.zendesk.com/hc/fi/articles/25077781212173-Miten-se-toimii",
    sv: "https://moziogroup.zendesk.com/hc/sv-se/articles/25157413183245-Hur-fungerar-det",
  },
  [URL_NAMES.COMPATIBLE_DEVICE]: {
    en_US:
      "https://moziogroup.zendesk.com/hc/en-us/articles/4420097778957-Is-my-device-compatible-with-eSIM-",
    it_IT:
      "https://moziogroup.zendesk.com/hc/it-it/articles/4420097778957-Quali-dispositivi-supportano-le-eSIM-",
    nl: "https://moziogroup.zendesk.com/hc/nl/articles/23059349320589-Is-mijn-telefoon-compatibel-met-eSIM",
    fr: "https://moziogroup.zendesk.com/hc/fr-fr/articles/22529840138765-Mon-appareil-est-il-compatible-avec-eSIM",
    de_DE:
      "https://moziogroup.zendesk.com/hc/de/articles/24147496081933-Ist-mein-Ger%C3%A4t-mit-eSIM-kompatibel",
    no_NO:
      "https://moziogroup.zendesk.com/hc/no/articles/24945719889165-Er-enheten-min-kompatibel-med-eSIM",
    da_DK:
      "https://moziogroup.zendesk.com/hc/da-dk/articles/25077506931341-Hvilke-enheder-underst%C3%B8tter-eSIM",
    fi_FI:
      "https://moziogroup.zendesk.com/hc/fi/articles/25079445393293-Onko-laitteeni-eSIM-yhteensopiva",
    sv: "https://moziogroup.zendesk.com/hc/sv-se/articles/25158212320525-%C3%84r-min-enhet-kompatibel-med-eSIM",
  },
  [URL_NAMES.FAQ]: {
    en_US:
      "https://moziogroup.zendesk.com/hc/en-us/sections/4420118355085-Mozio-Extras",
    it_IT:
      "https://moziogroup.zendesk.com/hc/it-it/sections/4420118355085-Mozio-Extras",
    nl: "https://moziogroup.zendesk.com/hc/nl/sections/4420118355085-Ondersteuning-voor-Mozio-Travel-Extras",
    fr: "https://moziogroup.zendesk.com/hc/fr-fr/sections/4420118355085-Assistance-pour-Mozio-Extras",
    de_DE:
      "https://moziogroup.zendesk.com/hc/de/sections/4420118355085-Mozio-Extra",
    no_NO:
      "https://moziogroup.zendesk.com/hc/no/sections/4420118355085-Mozio-Extras",
    da_DK:
      "https://moziogroup.zendesk.com/hc/da-dk/sections/4420118355085-Mozio-Extras",
    fi_FI:
      "https://moziogroup.zendesk.com/hc/fi/sections/4420118355085-Mozio-Extras",
    sv: "https://moziogroup.zendesk.com/hc/sv-se/sections/4420118355085-Vanliga-fr%C3%A5gor",
  },
};

export const getUrlByLocale = (name: UrlNames, locale: string): string => {
  const urls = urlMap[name];
  if (locale in urls) {
    return urls[locale as keyof typeof urls];
  }

  console.error(
    `No "${name}" URL is configured for the locale: ${locale}. Falling back to the default en_US.`,
  );
  return urls.en_US;
};
